import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "react-router";

import { SignupCard, type SignupDraft } from "@/features/auth/components/SignupCard";
import { ApiError, authApi } from "@/features/mail/api/client";

/**
 * 계정 만들기.
 *
 * 초대 코드는 없다. 누구나 만들 수 있다.
 *
 * 한때 여기서 집사(구독제) 등급을 고르게 했다. 지금은 고를 것이 하나뿐이라
 * 그 걸음을 뺐다 — 선택지가 하나인 화면을 세워두면 고르는 일처럼 보이지만
 * 실제로는 누르고 지나가는 관문일 뿐이다. (`docs/plan.md`)
 *
 * 서버가 가입 성공과 함께 세션 쿠키까지 붙여 보내므로 로그인을 따로 시키지
 * 않는다. 만들자마자 자기 메일함이 열린다.
 */
export function SignupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const meQuery = useQuery({ queryKey: ["me"], queryFn: authApi.me, retry: false });

  // 주소 뒤에 붙는 도메인. 서버가 정하는 값이라 화면에 박아두지 않는다.
  const configQuery = useQuery({
    queryKey: ["config"],
    queryFn: authApi.config,
    staleTime: Infinity,
  });

  const signup = useMutation({
    mutationFn: (draft: SignupDraft) =>
      authApi.signup({
        address: `${draft.localPart}@${configQuery.data?.mailDomain ?? ""}`,
        displayName: draft.displayName,
        password: draft.password,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      void navigate("/", { replace: true });
    },
  });

  if (meQuery.isSuccess) return <Navigate to="/" replace />;

  return (
    <div className="bg-paper flex h-full flex-col items-center justify-center gap-6">
      <SignupCard
        mailDomain={configQuery.data?.mailDomain ?? ""}
        pending={signup.isPending}
        error={
          signup.error instanceof ApiError
            ? signup.error.message
            : signup.error
              ? "계정을 만들지 못했어요"
              : null
        }
        onSubmit={(draft) => signup.mutate(draft)}
      />

      <p className="text-muted text-[13px]">
        이미 계정이 있나요?{" "}
        <Link to="/login" className="text-link hover:text-link-hover underline">
          로그인
        </Link>
      </p>
    </div>
  );
}
